import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, Handle, Position, ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import { Users, Table2, GitBranch, Clock3, MousePointerClick, Mail } from "lucide-react";
import { buildFlowGraph, normalizePlanState } from "./buildFlowGraph";

const NODE_STYLES = {
  role: { icon: Users, accent: "var(--brand-2)", wash: "#e0f2f8" },
  table: { icon: Table2, accent: "var(--brand)", wash: "var(--brand-wash)" },
  businessRule: { icon: GitBranch, accent: "#7c3aed", wash: "#f1e9fe" },
  scheduledJob: { icon: Clock3, accent: "var(--warn)", wash: "var(--warn-wash)" },
  uiAction: { icon: MousePointerClick, accent: "var(--brand-dark)", wash: "var(--brand-wash)" },
  email: { icon: Mail, accent: "#0891b2", wash: "#e0f2f8" },
};

function PlanNode({ type, data }) {
  const style = NODE_STYLES[type] || NODE_STYLES.table;
  const Icon = style.icon;

  return (
    <div
      style={{
        background: "#fff",
        border: `1.5px solid ${style.accent}`,
        borderRadius: 14,
        padding: "10px 14px",
        minWidth: 200,
        boxShadow: "0 1px 3px rgba(15,26,28,.08)",
        fontFamily: "inherit",
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: style.accent, border: "none" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 9,
            background: style.wash,
            color: style.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={15} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={data.label}
          >
            {data.label}
          </div>
          {data.subtitle && (
            <div style={{ fontSize: 11, color: "var(--muted)" }}>{data.subtitle}</div>
          )}
          {data.description && (
            <div style={{ fontSize: 11, color: "var(--muted)" }}>{data.description}</div>
          )}
        </div>
      </div>
      {Array.isArray(data.fields) && data.fields.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
          {data.fields.slice(0, 6).map((f) => (
            <span
              key={f.name}
              style={{
                fontSize: 10,
                fontWeight: 600,
                background: "var(--card-2)",
                color: "var(--ink-2)",
                borderRadius: 999,
                padding: "2px 8px",
              }}
            >
              {f.label || f.name}
            </span>
          ))}
          {data.fields.length > 6 && (
            <span style={{ fontSize: 10, color: "var(--muted)" }}>+{data.fields.length - 6} more</span>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: style.accent, border: "none" }} />
    </div>
  );
}

const nodeTypes = {
  role: (props) => <PlanNode type="role" {...props} />,
  table: (props) => <PlanNode type="table" {...props} />,
  businessRule: (props) => <PlanNode type="businessRule" {...props} />,
  scheduledJob: (props) => <PlanNode type="scheduledJob" {...props} />,
  uiAction: (props) => <PlanNode type="uiAction" {...props} />,
  email: (props) => <PlanNode type="email" {...props} />,
};

const defaultEdgeOptions = {
  style: { stroke: "var(--line)", strokeWidth: 1.5 },
  labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
  labelStyle: { fill: "var(--muted)", fontSize: 11, fontWeight: 600 },
};

// planSource: either a raw blueprint object or an assembled_blueprint object
// (see normalizePlanState) — pass whatever is most current for the session.
export default function FlowDiagram({ planSource, height = 360, emptyText = "Nothing to show yet." }) {
  const { nodes, edges } = useMemo(() => {
    const planState = normalizePlanState(planSource);
    const hasAnything =
      planState.tables.length ||
      planState.roles.length ||
      planState.businessRules.length ||
      planState.scheduledJobs.length ||
      planState.uiActions.length;
    return hasAnything ? buildFlowGraph(planState) : { nodes: [], edges: [] };
  }, [planSource]);

  if (nodes.length === 0) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--muted)",
          fontSize: 13,
          background: "var(--card-2)",
          borderRadius: 16,
          border: "1px dashed var(--line)",
        }}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <div style={{ height, borderRadius: 16, border: "1px solid var(--line)", overflow: "hidden" }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="var(--line)" gap={18} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
